// Move A → Z: the maths shared by the editor, the preview and the export page.
//
// This file is copied as-is (without the "export " words) into
// AnimationStudio2_API/Assets/Export/Export.js between the markers
// "// ==== motionCore start ====" and "// ==== motionCore end ====", so the exported video moves
// exactly like the editor. Plain functions only: no imports, no DOM.
//
// All positions are canvas pixels of the layer's top-left corner. A Motion (see layerMotion.js):
//   toXf, toYf, delay, duration, easing, path, hXf, hYf, bounces, bounciness, points, spin,
//   repeat ("once" | "pingpong" | "loop"), times (number of trips, 0 = until the layer ends),
//   pause (seconds to wait at each end), scaleTo (size at Z, 1 = unchanged), opacityTo (0..1),
//   autoFace (a character turns to face where it walks), walkFile (action played while moving).

export function motionNum(v, d) {
  var n = Number(v);
  return isFinite(n) ? n : d;
}

export function motionLerp(p, q, t) {
  return { x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t };
}

export function motionHopPoint(A, Z, Hd, hops, bounciness, p) {
  var n = Math.max(1, Math.min(12, Math.round(Number(hops) || 1)));
  var d = Math.min(1, Math.max(0.2, Number(bounciness) || 0.6));
  var w = [];
  var total = 0;
  var k;
  for (k = 0; k < n; k++) {
    w.push(Math.sqrt(Math.pow(d, k)));
    total += w[k];
  }
  // The handle is the top of the first hop.
  var firstTop = motionLerp(A, Z, w[0] / 2 / total);
  var V = { x: Hd.x - firstTop.x, y: Hd.y - firstTop.y };
  var base = motionLerp(A, Z, p);
  var at = p * total;
  k = 0;
  while (k < n - 1 && at > w[k]) {
    at -= w[k];
    k++;
  }
  var u = Math.min(1, Math.max(0, at / w[k]));
  var lift = Math.pow(d, k) * 4 * u * (1 - u);
  return { x: base.x + V.x * lift, y: base.y + V.y * lift };
}

export function motionCatmull(p0, p1, p2, p3, t) {
  var t2 = t * t;
  var t3 = t2 * t;
  var f = function (a, b, c, d) {
    return 0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
  };
  return { x: f(p0.x, p1.x, p2.x, p3.x), y: f(p0.y, p1.y, p2.y, p3.y) };
}

export var motionCurveCache = {};
export var motionCurveCacheSize = 0;

// A point at progress p along the smooth curve through the points, at constant speed.
export function motionCurvePoint(pts, p) {
  var key = pts
    .map(function (q) {
      return q.x.toFixed(1) + "," + q.y.toFixed(1);
    })
    .join(";");
  var s = motionCurveCache[key];
  if (!s) {
    s = [{ x: pts[0].x, y: pts[0].y, d: 0 }];
    for (var i = 0; i < pts.length - 1; i++) {
      var p0 = pts[Math.max(0, i - 1)];
      var p3 = pts[Math.min(pts.length - 1, i + 2)];
      for (var j = 1; j <= 24; j++) {
        var q = motionCatmull(p0, pts[i], pts[i + 1], p3, j / 24);
        var last = s[s.length - 1];
        s.push({ x: q.x, y: q.y, d: last.d + Math.hypot(q.x - last.x, q.y - last.y) });
      }
    }
    if (motionCurveCacheSize > 200) {
      motionCurveCache = {};
      motionCurveCacheSize = 0;
    }
    motionCurveCache[key] = s;
    motionCurveCacheSize++;
  }
  var len = s[s.length - 1].d;
  if (!(len > 0)) return { x: pts[0].x, y: pts[0].y };
  var target = p * len;
  var lo = 0;
  var hi = s.length - 1;
  while (hi - lo > 1) {
    var mid = (lo + hi) >> 1;
    if (s[mid].d < target) lo = mid;
    else hi = mid;
  }
  var seg = s[hi].d - s[lo].d;
  return motionLerp(s[lo], s[hi], seg > 0 ? (target - s[lo].d) / seg : 0);
}

/** Where the layer's top-left corner is at progress p (0..1) of the path, in canvas pixels. */
export function motionPoint(m, A, W, H, p) {
  var Z = { x: Number(m.toXf) * W, y: Number(m.toYf) * H };
  var path = m.path || "line";
  if ((path === "arc" || path === "bounce") && isFinite(Number(m.hXf)) && isFinite(Number(m.hYf))) {
    var Hd = { x: Number(m.hXf) * W, y: Number(m.hYf) * H };
    return path === "arc" ? motionHopPoint(A, Z, Hd, 1, 1, p) : motionHopPoint(A, Z, Hd, m.bounces || 3, m.bounciness, p);
  }
  if (path === "custom" && Array.isArray(m.points) && m.points.length) {
    var pts = [A].concat(
      m.points.map(function (q) {
        return { x: Number(q.xf) * W, y: Number(q.yf) * H };
      }),
      [Z]
    );
    return motionCurvePoint(pts, p);
  }
  return motionLerp(A, Z, p);
}

export function motionEase(m, u) {
  return m.easing === "smooth" ? u * u * (3 - 2 * u) : u;
}

/**
 * Where along the path the layer is `sec` seconds after it appeared:
 *   p       0..1 along the path (eased)
 *   dir     1 while going A → Z, -1 while coming back (back-and-forth)
 *   moving  true while it travels (not before the start, while pausing, or after the end)
 *   walkSec seconds of travelling so far (for the walk action's animation)
 *   started true once the move has begun
 */
export function motionState(m, sec) {
  var dur = Math.max(motionNum(m.duration, 0), 0.001);
  var pause = Math.max(0, motionNum(m.pause, 0));
  var repeat = m.repeat === "pingpong" || m.repeat === "loop" ? m.repeat : "once";
  var times = repeat === "once" ? 1 : Math.max(0, Math.round(motionNum(m.times, 0)));
  var t = sec - motionNum(m.delay, 0);
  if (t <= 0) return { p: 0, dir: 1, moving: false, walkSec: 0, started: false };
  var cycle = dur + pause;
  var leg = Math.floor(t / cycle);
  if (times > 0 && leg >= times) {
    // Finished: rests where the last trip ended.
    var lastLeg = times - 1;
    var endP = repeat === "pingpong" && lastLeg % 2 === 1 ? 0 : 1;
    return { p: endP, dir: repeat === "pingpong" && lastLeg % 2 === 1 ? -1 : 1, moving: false, walkSec: times * dur, started: true };
  }
  var local = t - leg * cycle;
  var travelling = local < dur;
  var u = travelling ? local / dur : 1;
  var back = repeat === "pingpong" && leg % 2 === 1;
  var p = motionEase(m, u);
  if (back) p = 1 - p;
  return { p: p, dir: back ? -1 : 1, moving: travelling, walkSec: leg * dur + Math.min(local, dur), started: true };
}

/**
 * Everything to draw at `sec` seconds after the layer appeared, for a layer resting at `rest`
 * on a canvas of W × H pixels: { dx, dy, rotate (deg), scale, opacity, flip, moving, walkSec }.
 */
export function motionFrame(m, rest, W, H, sec) {
  var st = motionState(m, sec);
  var at = motionPoint(m, rest, W, H, st.p);
  var scaleTo = motionNum(m.scaleTo, 1);
  var opacityTo = motionNum(m.opacityTo, 1);
  var flip = false;
  if (m.autoFace && st.started) {
    // Faces the way it travels; "as placed" faces the first step of the path.
    var e = 0.01;
    var p0 = motionPoint(m, rest, W, H, 0);
    var p1 = motionPoint(m, rest, W, H, e);
    var startDx = p1.x - p0.x;
    var a = motionPoint(m, rest, W, H, Math.max(0, Math.min(1 - e, st.p - (st.dir > 0 ? e : 0))));
    var b = motionPoint(m, rest, W, H, Math.max(e, Math.min(1, st.p + (st.dir > 0 ? 0 : e))));
    var nowDx = (b.x - a.x) * st.dir;
    if (Math.abs(startDx) > 0.01 && Math.abs(nowDx) > 0.01) flip = startDx > 0 !== nowDx > 0;
    else if (Math.abs(startDx) > 0.01 && st.dir < 0) flip = true;
  }
  return {
    dx: at.x - rest.x,
    dy: at.y - rest.y,
    rotate: motionNum(m.spin, 0) * 360 * st.p,
    scale: 1 + (scaleTo - 1) * st.p,
    opacity: Math.max(0, Math.min(1, 1 + (opacityTo - 1) * st.p)),
    flip: flip,
    moving: st.moving,
    walkSec: st.walkSec,
    started: st.started,
  };
}
